import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import FetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/Queries/FetchBeneficiaryContacts';
import fetchBeneficiaryContacts from '@Application/Beneficiaries/BeneficiaryContacts/BeneficiaryContacts/fetchBeneficiaryContacts';
import FetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/Queries/FetchStaffMembers';
import fetchStaffMembers from '@Application/Facilities/StaffMembers/StaffMembers/fetchStaffMembers';
import { PermissionEnum } from '@Application/Security/Roles';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import PlainDate from '@Domain/PlainDate';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { LocalInterviewParticipant } from '@Domain/Ports/LocalInterviews/LocalInterviewPort';
import { fieldWithoutMarginStyles } from '@Guidelines/Theme/commonPassthrough';
import { theme } from '@Guidelines/Theme/theme';
import ConsentFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/CreateInterviewConsentPage/FormData/ConsentFormData';
import BackLink from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/BackLink';
import useCapturePreparation from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/Hooks/useCapturePreparation';
import useLocalInterviewCreation from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Creations/Hooks/useLocalInterviewCreation';
import useSubjectModifiability from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Hooks/useSubjectModifiability';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import useCurrentOrganization from '@UI/Providers/CurrentOrganizationProvider/useCurrentOrganization';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import { css as cssToClassName } from '@emotion/css';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@verclam/toast-v2';
import { useDispatch, useSelector, useStoreQuery } from '@verclam/synapse';
import { Button, Calendar, Checkbox, Message, MultiSelect, Skeleton, Tag } from '@verclam/ui-kit';

const screenStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
	width: 100%;
	max-width: 45rem;
	margin: 0 auto;
`;

const titleRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const titleStyle = css`
	${theme.typography.heroTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const detailsCardStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	padding: 1.25rem;
	border-radius: ${theme.radii.xlarge};
	background-color: ${theme.colors.surface};
`;

const eyebrowStyle = css`
	${theme.typography.eyebrow}
	text-transform: uppercase;
	color: ${theme.colors.inkMuted};
`;

const fieldsRowStyle = css`
	display: flex;
	gap: 0.875rem;
	flex-wrap: wrap;
`;

const fieldStyle = css`
	flex: 1;
	min-width: 12.5rem;
`;

const scriptCardStyle = css`
	padding: 1.875rem;
	border-radius: ${theme.radii.xxlarge};
	background-color: ${theme.states.warning.surface};
	color: #4a3105;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		padding: 1.375rem;
	}
`;

const scriptStyle = css`
	margin: 0;
	font-family: ${theme.fonts.display};
	font-size: 1.3125rem;
	line-height: 1.62;
	letter-spacing: -0.01em;
`;

const hintStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.6;
	color: ${theme.colors.inkMuted};
`;

/** Class names, not Emotion styles: the kit's `rootContainerClassName` takes a plain class. */
const consentRowClassName = (isChecked: boolean): string => cssToClassName`
	background-color: ${theme.colors.surface};
	border-radius: ${theme.radii.large};
	padding: 1.125rem 1.25rem;
	border: 1.5px solid ${isChecked ? theme.colors.accent : theme.colors.borderStrong};
`;

const actionsRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.625rem;
	flex-wrap: wrap;
	justify-content: space-between;
`;

type Props = {
	readonly subject: SubjectAddress;
};

/**
 * The first step of a conversation recorded for a preparation: the date and the participants, then what
 * the dossier's type asks for before recording — a signed consent (the text read aloud and its
 * confirmation), a notice (one box: the participants were informed) or nothing — then the local interview is written to the device and its
 * recording opens. Confirming reads `Date.now()` once, as the consent's collection instant. No network
 * call is made here: the composite creation happens later, from the transfer handler. The interview's
 * name is its default, « Entretien du {date} ». The two participant referentials never gate the
 * screen: an unavailable one only disables its own field.
 */
const InterviewConsentContent: React.FC<Props> = ({ subject }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const toast = useToast();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const { currentOrganization } = useCurrentOrganization();
	const organizationId = currentOrganization?.id ?? null;
	const hasStaffReadPermission = useHasPermission(PermissionEnum.STAFF_READ);
	const { preparationId, preparation, preparationPath } = useCapturePreparation(subject);
	const { isKnown, canModify, isArchived } = useSubjectModifiability(subject);
	const isWriteRefused = isKnown && !canModify;
	const { createLocalInterview, isSubmitting, hasFailed } = useLocalInterviewCreation(subject);
	const { isBeneficiary, subjectId, typeKey } = subject;
	// A beneficiary always signs a consent; any other type says what it asks for. Without its type
	// (not loaded), the strictest screen applies.
	const typeInformationMode = useSelector(
		(state) => state.subjectTypesStore.types?.findByKey(typeKey)?.informationMode.value ?? null,
	);
	const informationMode = isBeneficiary ? 'signed_consent' : (typeInformationMode ?? 'signed_consent');
	const isConsentRequired = informationMode === 'signed_consent';
	const isConfirmationRequired = informationMode !== 'none';

	const [formData, setFormData] = React.useState(() => ConsentFormData.createEmpty());
	const [isConsentChecked, setIsConsentChecked] = React.useState(false);

	const { data: contacts, error: contactsError } = useStoreQuery({
		selector: (state) => state.beneficiaryContactsStore.contacts,
		stopOn: (value) => !isBeneficiary || (value !== null && value.beneficiaryId === subjectId),
		query: () => {
			if (!isBeneficiary) {
				return Promise.resolve();
			}

			return dispatch(fetchBeneficiaryContacts(new FetchBeneficiaryContacts(facilityId, subjectId)));
		},
		deps: [facilityId, subjectId, isBeneficiary],
	});

	// Guarded on `STAFF_READ`: a custom role without it would otherwise draw a 403. `Promise.resolve()`
	// is the established way to make `useStoreQuery` skip a request rather than dispatch with a null id.
	const { data: staffMembers, error: staffMembersError } = useStoreQuery({
		selector: (state) => state.staffMembersStore.staffMembers,
		stopOn: (value) => value !== null && value.organizationId === organizationId,
		query: () => {
			if (organizationId === null || !hasStaffReadPermission) {
				return Promise.resolve();
			}

			return dispatch(fetchStaffMembers(new FetchStaffMembers(organizationId)));
		},
		deps: [organizationId, hasStaffReadPermission],
	});

	const contactOptions = React.useMemo(() => contacts?.list ?? [], [contacts]);
	const staffOptions = React.useMemo(
		() => (staffMembers !== null ? staffMembers.forFacility(facilityId).list : []),
		[staffMembers, facilityId],
	);

	const breadcrumbItems = React.useMemo(
		() => [
			{ label: preparation?.label ?? 'Préparation', url: preparationPath },
			{ label: isConsentRequired ? 'Consentement' : 'Nouvel entretien' },
		],
		[preparation, preparationPath, isConsentRequired],
	);

	const handleConductedOnChange = React.useCallback((value: Date | null) => {
		setFormData((current) =>
			current.setConductedOn(value !== null ? PlainDate.fromCalendar(value) : null),
		);
	}, []);

	const handleParticipantContactIdsChange = React.useCallback((value: readonly number[]) => {
		setFormData((current) => current.setParticipantContactIds(value));
	}, []);

	const handleParticipantStaffMemberIdsChange = React.useCallback((value: readonly number[]) => {
		setFormData((current) => current.setParticipantStaffMemberIds(value));
	}, []);

	const handleRefuse = React.useCallback(() => {
		navigate(preparationPath);
		toast.current?.show({
			severity: 'info',
			summary: 'Entretien annulé : sans consentement, aucun enregistrement.',
		});
	}, [navigate, preparationPath, toast]);

	const handleStartRecording = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		if (isWriteRefused || formData.conductedOn === null) {
			return;
		}

		const contactParticipants: LocalInterviewParticipant[] = formData.participantContactIds.map((id) => ({
			kind: 'contact',
			id,
			label: contacts?.findById(id)?.fullName ?? `Contact n°${id}`,
		}));
		const staffParticipants: LocalInterviewParticipant[] = formData.participantStaffMemberIds.map(
			(id) => ({
				kind: 'staff',
				id,
				label: staffMembers?.findById(id)?.fullName ?? `Membre du personnel n°${id}`,
			}),
		);

		// The instant of collection, read once, here, and nowhere else: it is what will be sent to the
		// server days later as the consent's probative timestamp, or as the instant the participants
		// were informed.
		const confirmedAt = Date.now();

		createLocalInterview(
			{
				name: `Entretien du ${formData.conductedOn.toDisplay()}`,
				kind: InterviewKind.CONVERSATION,
				conductedOn: formData.conductedOn.toApi(),
				participants: [...contactParticipants, ...staffParticipants],
				preparationId,
				...(informationMode === 'notice' && { participantsInformedAt: confirmedAt }),
			},
			isConsentRequired ? confirmedAt : null,
		);
	}, [
		formData,
		isWriteRefused,
		contacts,
		staffMembers,
		createLocalInterview,
		preparationId,
		informationMode,
		isConsentRequired,
	]);

	if (preparation === null) {
		return (
			<React.Fragment>
				<SubjectBreadcrumb subject={subject} items={breadcrumbItems} />
				<Skeleton height="20rem" animation="wave" shape="rectangle" />
			</React.Fragment>
		);
	}

	return (
		<div css={screenStyle}>
			<SubjectBreadcrumb subject={subject} items={breadcrumbItems} />
			<BackLink to={preparationPath} label={preparation.label} />

			<div css={titleRowStyle}>
				<h1 css={titleStyle}>{isConsentRequired ? 'Consentement' : 'Nouvel entretien'}</h1>
				<ConditionalLoader data={isConsentRequired}>
					<Tag value="PASSAGE OBLIGÉ" severity="danger" />
				</ConditionalLoader>
			</div>

			<div css={detailsCardStyle}>
				<span css={eyebrowStyle}>DÉTAILS DE L'ENTRETIEN</span>
				<div css={fieldsRowStyle}>
					<div css={fieldStyle}>
						<Calendar
							name="conductedOn"
							label="Date"
							dateFormat="dd/mm/yy"
							disabled={isWriteRefused}
							value={formData.conductedOn !== null ? formData.conductedOn.toCalendar() : null}
							onChange={handleConductedOnChange}
							errorMessage={formData.getValidationMessage('conductedOn')}
							isFormFieldValid={formData.isValid('conductedOn')}
						/>
					</div>
					<ConditionalLoader data={isBeneficiary}>
						<div css={fieldStyle}>
							<MultiSelect<number>
								name="participantContactIds"
								label="Participants de l'entourage"
								options={contactOptions}
								optionLabel="fullName"
								optionValue="id"
								loading={contacts === null && contactsError === null}
								disabled={isWriteRefused || contactsError !== null}
								value={formData.participantContactIds}
								onChange={handleParticipantContactIdsChange}
								errorMessage={formData.getValidationMessage('participantContactIds')}
								isFormFieldValid={formData.isValid('participantContactIds')}
							/>
							<ConditionalLoader data={contactsError !== null}>
								<p css={hintStyle}>Les contacts du dossier n'ont pas pu être chargés.</p>
							</ConditionalLoader>
						</div>
					</ConditionalLoader>
					<WithPermission permission={PermissionEnum.STAFF_READ}>
						<div css={fieldStyle}>
							<MultiSelect<number>
								name="participantStaffMemberIds"
								label="Personnel de la structure"
								options={staffOptions}
								optionLabel="fullName"
								optionValue="id"
								loading={staffMembers === null && staffMembersError === null}
								disabled={isWriteRefused || staffMembersError !== null}
								value={formData.participantStaffMemberIds}
								onChange={handleParticipantStaffMemberIdsChange}
								errorMessage={formData.getValidationMessage('participantStaffMemberIds')}
								isFormFieldValid={formData.isValid('participantStaffMemberIds')}
							/>
							<ConditionalLoader data={staffMembersError !== null}>
								<p css={hintStyle}>Le personnel de la structure n'a pas pu être chargé.</p>
							</ConditionalLoader>
						</div>
					</WithPermission>
				</div>
			</div>

			<ConditionalLoader data={isConsentRequired}>
				<div css={scriptCardStyle}>
					<p css={scriptStyle}>
						« Je vais enregistrer notre échange pour rédiger votre document d'accompagnement.
						L'enregistrement sera conservé de façon sécurisée, puis effacé. Vous pouvez refuser,
						et me demander de l'arrêter à tout moment. Êtes-vous d'accord ? »
					</p>
				</div>
				<p css={hintStyle}>
					Texte destiné à être lu à voix haute. Sa formulation relève de la structure et de son DPO.
				</p>
			</ConditionalLoader>

			<ConditionalLoader data={isConfirmationRequired}>
				<Checkbox
					rootContainerClassName={consentRowClassName(isConsentChecked)}
					name="consentRead"
					label={
						isConsentRequired
							? "J'ai lu ce texte à voix haute, l'accord a été donné, et les personnes présentes ont été informées."
							: "Les participants ont été informés de l'enregistrement."
					}
					checked={isConsentChecked}
					onChange={setIsConsentChecked}
					pt={fieldWithoutMarginStyles}
				/>
			</ConditionalLoader>

			<ConditionalLoader data={isArchived}>
				<Message
					severity="warn"
					text="Ce dossier est archivé : la création d'un entretien n'est plus possible."
				/>
			</ConditionalLoader>

			<ConditionalLoader data={hasFailed}>
				<Message severity="error" text="L'entretien n'a pas pu être préparé sur cet appareil." />
			</ConditionalLoader>

			<div css={actionsRowStyle}>
				<ConditionalLoader data={isConsentRequired}>
					<Button
						type="button"
						label="Le consentement n'est pas obtenu"
						outlined
						severity="secondary"
						disabled={isSubmitting}
						onClick={handleRefuse}
					/>
				</ConditionalLoader>
				<ConditionalLoader data={!isWriteRefused}>
					<Button
						type="button"
						label="Commencer l'enregistrement"
						loading={isSubmitting}
						disabled={(isConfirmationRequired && !isConsentChecked) || isSubmitting}
						onClick={handleStartRecording}
					/>
				</ConditionalLoader>
			</div>
		</div>
	);
};

export default InterviewConsentContent;
